package com.corbettcode.allaboutrocks.pages.typesof

import androidx.compose.runtime.Composable
import com.corbettcode.allaboutrocks.FIGURE_IMAGE_MAX_WIDTH
import com.corbettcode.allaboutrocks.HERO_IMAGE_MAX_WIDTH
import com.corbettcode.allaboutrocks.HeadlineTextStyle
import com.corbettcode.allaboutrocks.HeroContainerStyle
import com.corbettcode.allaboutrocks.ImageCreditStyle
import com.corbettcode.allaboutrocks.PageHeadlineStyle
import com.corbettcode.allaboutrocks.PageListStyle
import com.corbettcode.allaboutrocks.PageSectionTitleStyle
import com.corbettcode.allaboutrocks.components.layouts.PageLayoutData
import com.corbettcode.allaboutrocks.utility.textColor
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment.CenterHorizontally
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.layout.HorizontalDivider
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.css.StyleScope
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.width
import org.jetbrains.compose.web.dom.B
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Em
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Table
import org.jetbrains.compose.web.dom.Tbody
import org.jetbrains.compose.web.dom.Td
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Th
import org.jetbrains.compose.web.dom.Thead
import org.jetbrains.compose.web.dom.Tr
import org.jetbrains.compose.web.dom.Ul

// --- Constants ---
//private val HERO_IMAGE_MAX_WIDTH = 600.px
//private val FIGURE_IMAGE_MAX_WIDTH = 600.px

@InitRoute
fun initTypesOfIgneous(ctx: InitRouteContext) {
    ctx.data.add(PageLayoutData("Types of Rocks"))
}

private val igneousKeyCharacteristics = listOf(
    "Interlocking crystals" to "Grains grow into each other as the melt cools, so there are no rounded, water-worn grains and no cement holding things together."
    , "No layers, no fossils" to "Igneous rock cools from a melt, so nothing that was alive survives inside it. Banding, when it shows up, comes from flow or from crystals settling, not from bedding."
    , "Grain size tells the story" to "Coarse grains you can pick out by eye mean slow cooling deep underground. Grains too small to see, or none at all, mean fast cooling at the surface."
    , "Color hints at composition" to "Pale rocks are usually felsic (silica- and feldspar-rich); dark rocks are usually mafic (iron- and magnesium-rich) and noticeably heavier in the hand."
    , "Hardness" to "Typically 5–7 on the Mohs scale, depending on the minerals present. Quartz-bearing types will scratch glass."
)

/** How the melt cooled, which is the first split any igneous rock falls into. */
private val igneousFamilies = listOf(
    "Intrusive (plutonic)" to "Magma cooled slowly inside the crust, giving crystals time to grow large enough to see. Granite, diorite, and gabbro are the classic examples."
    , "Extrusive (volcanic)" to "Lava cooled quickly at the surface, freezing the texture fine-grained or glassy. Basalt, rhyolite, obsidian, and pumice all form this way."
)

/** How geologists sort igneous rock by silica content, from palest to darkest. */
private val igneousCompositionColumns = listOf("Type", "Silica Content", "Color", "Example")

private val igneousCompositionRows = listOf(
    listOf("Felsic", "High (>65%)", "Light", "Granite, Rhyolite")
    , listOf("Intermediate", "Medium (55–65%)", "Medium", "Diorite, Andesite")
    , listOf("Mafic", "Low (45–55%)", "Dark", "Gabbro, Basalt")
    , listOf("Ultramafic", "Very low (<45%)", "Very dark", "Peridotite")
)

/** Named igneous rocks, paired with what sets each one apart. */
private val igneousExamples = listOf(
    "Granite" to "Coarse-grained and speckled with quartz, feldspar, and mica. The most familiar intrusive rock, a favorite for countertops and buildings, and the backbone of many mountain ranges."
    , "Basalt" to "Fine-grained, dark, and heavy; the most common rock on Earth's surface. Floors the ocean basins and builds shield volcanoes and lava plateaus."
    , "Obsidian" to "Volcanic glass that cooled too fast to crystallize at all. Breaks with razor-sharp conchoidal fractures, which is why it was prized for blades and points."
    , "Pumice" to "Frothy volcanic glass full of gas bubbles. Light enough that it will float on water."
    , "Scoria" to "Darker and denser than pumice, with larger bubbles. Common as red and black cinder around volcanic vents."
    , "Rhyolite" to "The fine-grained equivalent of granite. Often banded or flow-layered, and a frequent host for agate and thundereggs."
    , "Andesite" to "Medium-toned volcanic rock typical of continental arc volcanoes, including much of the Cascades."
    , "Gabbro" to "The coarse-grained equivalent of basalt, dark and dense, formed in deep magma chambers."
    , "Diorite" to "Salt-and-pepper intrusive rock sitting between granite and gabbro in composition."
    , "Peridotite" to "Olive-green and very dense, made mostly of olivine. A piece of the mantle brought to the surface, and the source rock for peridot."
)

/** Why the type matters beyond identifying it in the field. */
private val igneousSignificance = listOf(
    "They record Earth's history" to "Their composition and age reveal how continents formed and how tectonic plates move."
    , "They are economically valuable" to "Granite is a prized building material, and igneous processes concentrate gold, copper, and platinum."
    , "They shape landscapes" to "Volcanic eruptions create new land, islands, and the fertile soils that follow."
    , "They help us study other planets" to "Basaltic rock on the Moon and Mars tells us about conditions beyond Earth."
)

private fun StyleScope.igneousTableCell() {
    property("border", "1px solid currentColor")
    padding(0.5.cssRem)
    property("text-align", "left")
}

@Composable
private fun IgneousFigure(src: String, alt: String, caption: String, credit: String) {
    Column(
        Modifier
            .fillMaxWidth()
            .gap(0.5.cssRem)
        , horizontalAlignment = CenterHorizontally
    ) {
        Image(
            src = src
            , alt = alt
            , modifier = Modifier
                .maxWidth(FIGURE_IMAGE_MAX_WIDTH)
                .fillMaxWidth()  // shrinks naturally on small screens
        )
        P { Em { Text(caption) } }
        SpanText(credit, ImageCreditStyle.toModifier())
    }
}

@Composable
private fun IgneousSectionTitle(text: String) {
    SpanText(
        text
        , PageSectionTitleStyle.toModifier()
            .color(textColor())
    )
}

@Composable
private fun TypesOfIgneousHeadline() {
    Div(HeadlineTextStyle.toAttrs()) {
        Column {
            SpanText(
                "Igneous Rocks: The Foundation of Earth's Crust"
                , PageHeadlineStyle.toModifier()
                    .color(textColor())
                    .align(alignment = CenterHorizontally)
            )
        }
    }
}

@Composable
private fun TypesOfIgneousHero() {

    TypesOfIgneousHeadline()

    // 1 column on mobile, 2 columns on md+ (image left, text right)
    SimpleGrid(
        numColumns = numColumns(1, md = 2)
        , modifier = HeroContainerStyle
            .toModifier()
            .fillMaxWidth()
            .gap(2.cssRem)
    ) {
        Box {
            Column(Modifier.gap(2.cssRem)) {
                Image(
                    src = "/images/typesof/igneous/puu_oo_volcanic_cone.jpg"
                    , alt = "Pu'u 'Ō'ō volcanic cone at dusk"
                    , modifier = Modifier
                        .maxWidth(HERO_IMAGE_MAX_WIDTH)
                        .fillMaxWidth()  // shrinks naturally on small screens
                )
                P { Em { Text("Pu'u 'Ō'ō, a volcanic cone on Kilauea, Hawaii.") } }
                SpanText(
                    "Photo: G.E. Ulrich, USGS, public domain"
                    , ImageCreditStyle.toModifier()
                )
            }
        }

        Column(Modifier.gap(2.cssRem)) {
            P {
                Text("Igneous rocks are one of the three major rock types, alongside sedimentary and metamorphic rocks. They form when molten material cools and solidifies, making them the most fundamental rocks on Earth — quite literally, since they make up the majority of our planet's crust and were the first rocks to form as Earth cooled billions of years ago.")
            }

            IgneousSectionTitle("What Are Igneous Rocks?")
            P {
                Text("The word \"igneous\" comes from the Latin ignis, meaning \"fire.\" That name reflects their origin: igneous rocks are born from intense heat, forming when magma (molten rock beneath the surface) or lava (molten rock that has erupted onto the surface) cools and crystallizes.")
            }
        }
    }

    HorizontalDivider(Modifier
        .fillMaxWidth()
        .margin(topBottom = 2.cssRem)
    )

    Column(
        Modifier
            .fillMaxWidth()
            .gap(2.cssRem)
    ) {
        IgneousSectionTitle("How They Form")
        P {
            Text("Rock melts where the crust and upper mantle get hot enough, or where pressure drops enough to let already-hot rock turn liquid — above subduction zones, along mid-ocean ridges, and over mantle hot spots. The melt is lighter than the rock around it, so it rises. From there, where and how quickly it cools decides everything else about the rock.")
        }

        Ul(PageListStyle.toAttrs()) {
            igneousFamilies.forEach { (name, description) ->
                Li {
                    B { Text(name) }
                    Text(" — $description")
                }
            }
        }

        IgneousFigure(
            src = "/images/typesof/igneous/Granite-rock.webp"
            , alt = "Granite sample showing interlocking crystals"
            , caption = "Granite — a coarse-grained intrusive rock with visible crystals of quartz, feldspar, and mica."
            , credit = "Photo: Granite Rock Sample. Photo: James St. John"
        )

        IgneousFigure(
            src = "/images/typesof/igneous/obsidian_oregon.jpg"
            , alt = "Obsidian sample from Oregon"
            , caption = "Obsidian from Lake County, Oregon — a glassy volcanic rock formed when lava cools almost instantly."
            , credit = "Photo: Wikimedia Commons, public domain"
        )

        IgneousSectionTitle("Classifying Igneous Rocks")
        P {
            Text("Geologists sort igneous rocks on two axes: texture, which is set by cooling rate (coarse, fine, glassy, or porphyritic), and composition, which reflects the chemistry of the original magma. Silica content is the usual measure, and it tracks closely with how light or dark the rock looks.")
        }

        Div(attrs = {
            style {
                property("overflow-x", "auto")
                width(100.percent)
            }
        }) {
            Table(attrs = {
                style {
                    property("border-collapse", "collapse")
                    width(100.percent)
                }
            }) {
                Thead {
                    Tr {
                        igneousCompositionColumns.forEach { header ->
                            Th(attrs = { style { igneousTableCell() } }) { Text(header) }
                        }
                    }
                }
                Tbody {
                    igneousCompositionRows.forEach { row ->
                        Tr {
                            row.forEach { cell ->
                                Td(attrs = { style { igneousTableCell() } }) { Text(cell) }
                            }
                        }
                    }
                }
            }
        }

        IgneousSectionTitle("Key Characteristics")

        Ul(PageListStyle.toAttrs()) {
            igneousKeyCharacteristics.forEach { (name, description) ->
                Li {
                    B { Text(name) }
                    Text(": $description")
                }
            }
        }

        IgneousSectionTitle("Common Examples")

        Ul(PageListStyle.toAttrs()) {
            igneousExamples.forEach { (name, description) ->
                Li {
                    B { Text(name) }
                    Text(" — $description")
                }
            }
        }

        IgneousFigure(
            src = "/images/typesof/igneous/pumice.jpg"
            , alt = "Pumice stone sample"
            , caption = "Pumice — so full of gas bubbles that it can float on water."
            , credit = "Photo: Benjamint444, CC BY-SA 3.0, via Wikimedia Commons"
        )

        IgneousFigure(
            src = "/images/typesof/igneous/giants_causeway_basalt_columns.jpg"
            , alt = "Basalt columns at the Giant's Causeway"
            , caption = "Basalt columns at the Giant's Causeway, Northern Ireland — a striking example of extrusive igneous rock."
            , credit = "Photo: Gunther Tschuch, CC BY-SA 4.0, via Wikimedia Commons"
        )

        IgneousSectionTitle("Why Igneous Rocks Matter")
        P {
            Text("Igneous rocks are more than geological curiosities — they are essential to understanding the planet:")
        }

        Ul(PageListStyle.toAttrs()) {
            igneousSignificance.forEach { (name, description) ->
                Li {
                    B { Text(name) }
                    Text(": $description")
                }
            }
        }

        IgneousSectionTitle("The Rock Cycle Connection")
        P {
            Text("Igneous rocks do not stay igneous forever. Through weathering, erosion, and heat and pressure, they can transform into sedimentary or metamorphic rock — and eventually melt back into magma to begin again. That continuous process, the rock cycle, is what links all three rock types together.")
        }

        IgneousFigure(
            src = "/images/typesof/igneous/rock_cycle.png"
            , alt = "Diagram of the rock cycle"
            , caption = "The rock cycle — igneous, sedimentary, and metamorphic rocks transform into one another over time."
            , credit = "Diagram: U.S. National Park Service, public domain"
        )

        IgneousSectionTitle("For Rockhounds")
        P {
            Text("Volcanic rock is where a lot of the good material hides. Gas pockets in basalt and rhyolite fill with silica over time and become the agates, jaspers, and thundereggs collectors hunt for, and weathered flows are the easiest place to find them loose. Obsidian fields, old cinder pits, and roadcuts through lava flows are all worth a stop — just check land ownership and collecting rules before you fill a bucket.")
        }

        IgneousSectionTitle("In Short")
        P {
            Text("Igneous rocks are the building blocks of Earth's crust and the starting point of the rock cycle. Whether you are standing on a granite mountain, walking across a basalt ocean floor, or holding a piece of volcanic obsidian, you are touching the fiery processes that have shaped this world for billions of years.")
        }
    }
}

@Page("/types_of_rocks/igneous")
@Layout(".components.layouts.PageLayout")
@Composable
fun TypesOfIgneousPage() {
    TypesOfIgneousHero()
}
